/**
 * LeadFormModal — create / edit a lead (Leads v2 contract).
 * Reuses the toolbox modal shell classes (.modal-overlay/.modal/.form-group…)
 * which are global once TasksRemindersPage.css is loaded.
 */
import { useEffect, useState } from "react";
import { UserPlus } from "@phosphor-icons/react";
import ModalCloseButton from "../ui/ModalCloseButton";
import DatePicker from "../ui/DatePicker";
import TimePicker from "../ui/TimePicker";
import ReferralSourceField from "../patients/ReferralSourceField";
import LoaderLoop from "../shared/loader/LoaderLoop";
import { HelpButton } from "../shared";
import { LEAD_LOST_REASONS } from "../../utils/leadContract";
import { combineDateTime, splitDateTime } from "./leadsUtils";

export const EMPTY_LEAD_FORM = {
  name: "",
  phone: "",
  email: "",
  referralSource: "",
  status: "new",
  lostReason: "",
  lostNote: "",
  followDate: "",
  followTime: "",
  proposedSessionCost: "",
  agreedSessionCost: "",
  preferredDaysAndTimes: "",
  notes: "",
};

export const leadToForm = (lead) => {
  const { date, time } = splitDateTime(lead?.nextFollowUpAt);
  return {
    name: lead?.name || "",
    phone: lead?.phone || "",
    email: lead?.email || "",
    referralSource: lead?.referralSource || "",
    status: lead?.status || "new",
    lostReason: lead?.lostReason || "",
    lostNote: lead?.lostNote || "",
    followDate: date,
    followTime: time,
    proposedSessionCost: lead?.proposedSessionCost ?? "",
    agreedSessionCost: lead?.agreedSessionCost ?? "",
    preferredDaysAndTimes: lead?.preferredDaysAndTimes || "",
    notes: lead?.notes || "",
  };
};

/** Form state → leadData for createLead/updateLead (+ owner object). */
export const formToPayload = (form) => {
  const nextFollowUpAt = combineDateTime(form.followDate, form.followTime);
  return {
    leadData: {
      name: form.name.trim(),
      phone: form.phone.trim() || null,
      email: form.email.trim() || null,
      referralSource: (form.referralSource || "").trim() || null,
      status: form.status || "new",
      lostReason: form.status === "lost" ? form.lostReason || "other" : null,
      lostNote: form.status === "lost" ? form.lostNote.trim() || null : null,
      nextFollowUpAt,
      proposedSessionCost: String(form.proposedSessionCost ?? "").trim() || null,
      agreedSessionCost: String(form.agreedSessionCost ?? "").trim() || null,
      preferredDaysAndTimes: form.preferredDaysAndTimes.trim() || null,
      notes: form.notes.trim() || null,
    },
  };
};

function LeadFormModal({
  isOpen,
  mode, // "create" | "edit"
  initialForm,
  statuses,
  therapistProfile,
  isSaving = false,
  onClose,
  onSave, // (form) => Promise
}) {
  const [form, setForm] = useState(EMPTY_LEAD_FORM);

  useEffect(() => {
    if (!isOpen) return;
    setForm(initialForm || EMPTY_LEAD_FORM);
  }, [isOpen, initialForm]);

  if (!isOpen) return null;

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const canSave = form.name.trim().length > 0 && !isSaving;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal ld-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="tb-modal-title">
            <span className="tb-modal-icon">
              <UserPlus size={18} weight="fill" />
            </span>
            <div className="ld-modal-heading">
              <span className="ld-eyebrow">מתעניינים</span>
              <h3>{mode === "edit" ? "עריכת מתעניין" : "מתעניין חדש"}</h3>
            </div>
          </div>
          <div className="modal-header-actions">
            <HelpButton featureKey="lead_modal" />
            <ModalCloseButton onClick={onClose} />
          </div>
        </div>

        <div className="modal-body">
          <div className="form-row">
            <div className="form-group">
              <label className="required">שם מלא</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => set("name")(e.target.value)}
                placeholder="שם המתעניין/ת"
                maxLength={100}
                autoFocus
              />
            </div>
            <div className="form-group">
              <label>טלפון</label>
              <input
                type="tel"
                className="ld-ltr"
                value={form.phone}
                onChange={(e) => set("phone")(e.target.value)}
                placeholder="050-0000000"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>אימייל</label>
              <input
                type="email"
                className="ld-ltr"
                value={form.email}
                onChange={(e) => set("email")(e.target.value)}
                placeholder="name@example.com"
              />
            </div>
            <div className="form-group">
              <label>מקור הפניה</label>
              <ReferralSourceField
                value={form.referralSource}
                onChange={set("referralSource")}
                therapistProfile={therapistProfile}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>סטטוס</label>
              <select value={form.status} onChange={(e) => set("status")(e.target.value)}>
                {statuses
                  .filter((s) => s.value !== "converted" || form.status === "converted")
                  .map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
              </select>
            </div>
            <div className="form-group">
              <label>מעקב הבא — תאריך</label>
              <DatePicker value={form.followDate} onChange={(e) => set("followDate")(e.target.value)} />
            </div>
          </div>

          {form.status === "lost" && (
            <div className="form-row ld-lost-row">
              <div className="form-group">
                <label>סיבה</label>
                <select value={form.lostReason} onChange={(e) => set("lostReason")(e.target.value)}>
                  <option value="">בחר/י סיבה</option>
                  {LEAD_LOST_REASONS.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>הערה</label>
                <input
                  type="text"
                  value={form.lostNote}
                  onChange={(e) => set("lostNote")(e.target.value)}
                  placeholder="אופציונלי"
                  maxLength={500}
                />
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label>שעת המעקב (אופציונלי)</label>
              <TimePicker value={form.followTime} onChange={(e) => set("followTime")(e.target.value)} disabled={!form.followDate} />
            </div>
            <div className="form-group" />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>עלות לפגישה שהוצעה</label>
              <input
                type="number"
                className="ld-ltr"
                min="0"
                value={form.proposedSessionCost}
                onChange={(e) => set("proposedSessionCost")(e.target.value)}
                placeholder="₪"
              />
            </div>
            <div className="form-group">
              <label>עלות לפגישה שסוכמה</label>
              <input
                type="number"
                className="ld-ltr"
                min="0"
                value={form.agreedSessionCost}
                onChange={(e) => set("agreedSessionCost")(e.target.value)}
                placeholder="₪"
              />
            </div>
          </div>

          <div className="form-group">
            <label>ימים ושעות עדיפים</label>
            <input
              type="text"
              value={form.preferredDaysAndTimes}
              onChange={(e) => set("preferredDaysAndTimes")(e.target.value)}
              placeholder="לדוגמה: ימי ב׳/ד׳ אחה״צ"
              maxLength={300}
            />
          </div>

          <div className="form-group">
            <label>הערות</label>
            <textarea
              value={form.notes}
              onChange={(e) => set("notes")(e.target.value)}
              rows={3}
              placeholder="הערות כלליות על המתעניין/ת"
              maxLength={2000}
            />
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose} disabled={isSaving}>
            ביטול
          </button>
          <button className="btn-primary" onClick={() => onSave(form)} disabled={!canSave}>
            {isSaving && <LoaderLoop size="xs" />}
            <span>{mode === "edit" ? "שמור שינויים" : "צור מתעניין"}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default LeadFormModal;
